import { useEffect, useState } from 'react';

import { MinusOutlined, PlusOutlined } from '@ant-design/icons';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  MouseSensor,
  pointerWithin,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { Col, Flex, Input, List, Modal, type ModalProps, Row, Spin, Typography } from 'antd';
import cn from 'classnames';
import { createAsyncSelect } from 'components/AsyncSelect';
import { ModalDivider } from 'components/modal-divider';
import { useIntersectionObserver } from 'hooks/reactuse';
import { useDebouncedState } from 'hooks/useDebouncedState';
import { useMessageLogger } from 'hooks/useMessageLogger';
import { type User } from 'models/user';
import { useAppSelector } from 'services/redux/configureStore';
import { teamsApi } from 'services/rtk/teams';
import { usersApi } from 'services/rtk/users';

import styles from './ChangeAssignedModal.module.css';
import { DraggableUserItem } from './components/DraggableUserItem';
import { DroppableContainer } from './components/DroppableContainer';
import { useInfiniteUsers } from './hooks/useInfiniteUsers';

const { Text } = Typography;

const TeamSelect = createAsyncSelect(teamsApi.useFetchTeamsQuery);

interface ChangeAssignedModalProps extends ModalProps {
  id: User['id'];
  assignedUsers: User['assignedUsers'];
  onCancel: () => void;
}

export const ChangeAssignedModal = ({
  id,
  assignedUsers,
  onCancel,
  open,
  ...modalProps
}: ChangeAssignedModalProps) => {
  const [searchQuery, debouncedSearchQuery, setSearchQuery] = useDebouncedState<string>('');
  const [selectedUsers, setSelectedUsers] = useState(assignedUsers);
  const [activeUser, setActiveUser] = useState<NonNullable<User['assignedUsers']>[number] | null>(null);
  const [selectedTeam, setSelectedTeam] = useState<{ id: number; name: string } | null>(null);

  const { searchedUsers, loadNext, hasMore, isUsersFetching, removeFromSearched, addToSearched } =
    useInfiniteUsers(debouncedSearchQuery, selectedTeam?.id);

  const { ref, inView } = useIntersectionObserver<HTMLDivElement>({ threshold: 0.1 });

  const isDarkMode = useAppSelector((state) => state.theme.isDarkMode);

  const assignedUsersIds = selectedUsers?.map((au) => au.id) || [];
  const searchedAndFilteredUsers = searchedUsers.filter((user) => !assignedUsersIds.includes(user.id));

  const [updateUser, updateUserConfig] = usersApi.useUpdateUserMutation();

  const onOk = () => {
    const newAssigned = selectedUsers?.map((u) => u.id);
    updateUser({ id, assignedUserIds: newAssigned });
  };

  useMessageLogger({
    error: updateUserConfig.error,
    status: updateUserConfig.status,
    successMessage: 'Доступные пользователи обновлены',
    errorMessage: 'Не удалось назначить пользователей',
    onSuccess: onCancel,
  });

  useEffect(() => {
    if (!open) {
      setSelectedUsers(assignedUsers);
      setSearchQuery('');
      setActiveUser(null);
      setSelectedTeam(null);
    }
  }, [open]);

  useEffect(() => {
    if (inView && hasMore && !isUsersFetching) {
      loadNext();
    }
  }, [inView, hasMore, isUsersFetching]);

  const addToAssigned = (user: NonNullable<User['assignedUsers']>[number]) => {
    removeFromSearched(user.id);
    setSelectedUsers((prev) => {
      if (prev) {
        return [user, ...prev];
      } else {
        return [user];
      }
    });
  };

  const removeFromAssigned = (user: NonNullable<User['assignedUsers']>[number]) => {
    addToSearched(user);
    setSelectedUsers((prev) => {
      if (prev) {
        return prev.filter((u) => u.id !== user.id);
      } else {
        return [];
      }
    });
  };

  const onDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const id = Number(active.id);
    const user = searchedAndFilteredUsers.find((u) => u.id === id) || selectedUsers?.find((u) => u.id === id);
    if (user) setActiveUser(user);
  };

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveUser(null);
    if (!over) return;

    if (over.id === 'assignedUsers' && !assignedUsersIds.includes(Number(active.id))) {
      const movedUser = searchedAndFilteredUsers.find((u) => u.id === Number(active.id));
      if (movedUser) addToAssigned(movedUser);
    }

    if (over.id === 'allUsers' && assignedUsersIds.includes(Number(active.id))) {
      const movedUser = selectedUsers?.find((u) => u.id === Number(active.id));
      if (movedUser) removeFromAssigned(movedUser);
    }
  };

  const sensors = useSensors(useSensor(MouseSensor));

  return (
    <Modal
      open={open}
      title={'Изменить доступных пользователей'}
      onOk={onOk}
      onCancel={onCancel}
      okText={'Изменить'}
      width={900}
      centered={true}
      className={styles.modal}
      {...modalProps}
    >
      <ModalDivider type={'top'} className={styles.divider} />
      <Flex gap={3}>
        <Input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={'Найти пользователя'}
          className={'wide'}
          allowClear
        />
        <TeamSelect
          placeholder={'Фильтр по звену'}
          value={selectedTeam}
          onChange={setSelectedTeam}
          style={{ width: 350 }}
        />
      </Flex>
      <DndContext
        collisionDetection={pointerWithin}
        onDragEnd={onDragEnd}
        onDragStart={onDragStart}
        sensors={sensors}
      >
        <Row className={cn(styles.lists, { [styles.dark]: isDarkMode })}>
          <Col span={12}>
            <Text className={styles.listLabel}>Все пользователи</Text>
            <DroppableContainer id={'allUsers'}>
              <List
                itemLayout={'horizontal'}
                dataSource={searchedAndFilteredUsers}
                className={styles.searchList}
                renderItem={(item, index) => {
                  const isLast = searchedAndFilteredUsers.length - 1 === index;

                  return (
                    <>
                      <DraggableUserItem
                        key={item.id}
                        user={item}
                        icon={<PlusOutlined />}
                        onClick={() => addToAssigned(item)}
                        isDragging={activeUser?.id === item.id}
                      />

                      {isLast && <div ref={ref} />}
                      {isLast && hasMore && (
                        <div className={styles.loaderWrapper}>
                          <Spin />
                        </div>
                      )}
                    </>
                  );
                }}
              />
            </DroppableContainer>
          </Col>
          <Col span={12}>
            <Text className={styles.listLabel}>Доступные</Text>
            <DroppableContainer id={'assignedUsers'}>
              <List
                itemLayout={'horizontal'}
                dataSource={selectedUsers}
                className={styles.assignedList}
                renderItem={(item) => (
                  <DraggableUserItem
                    key={item.id}
                    user={item}
                    icon={<MinusOutlined />}
                    onClick={() => removeFromAssigned(item)}
                    isDragging={activeUser?.id === item.id}
                  />
                )}
              />
            </DroppableContainer>
          </Col>
        </Row>
        <DragOverlay>
          {activeUser ? (
            <List>
              <DraggableUserItem
                user={activeUser}
                icon={assignedUsersIds.includes(activeUser.id) ? <MinusOutlined /> : <PlusOutlined />}
                onClick={() => {}}
                isDragging={false}
                className={cn(styles.overlay, { [styles.lightBg]: !isDarkMode, [styles.darkBg]: isDarkMode })}
              />
            </List>
          ) : null}
        </DragOverlay>
      </DndContext>
      <ModalDivider type={'bottom'} className={styles.divider} />
    </Modal>
  );
};
